import { useEffect, useRef, useState } from 'react'
import { ProcessingStep } from 'api/clients/ChatClient'
import { Text } from 'blaze-ui/Text/Text'
import { Check02 } from 'blaze-ui/icons/16/Check02'
import { ChevronUp, ChevronDown, ChevronRightSmall } from 'blaze-ui/icons/20'
import { Spinner } from 'common/Spinner/Spinner'
import Styles from './ProcessingSteps.module.scss'

interface ProcessingStepsProps {
  steps: ProcessingStep[]
  estimatedMinutes?: number
  isNextGen?: boolean
  isCompleted?: boolean
}

const formatElapsed = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

const StepTimer = ({ estimatedMinutes = 2, isNextGen = false }: { estimatedMinutes?: number; isNextGen?: boolean }) => {
  const startTimeRef = useRef(Date.now())
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000))
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  if (isNextGen) {
    return (
      <Text variant="metadata" color="var(--dark-40)">
        {formatElapsed(elapsed)} / {estimatedMinutes} m
      </Text>
    )
  }

  return (
    <span className={Styles.timer}>
      {formatElapsed(elapsed)} / {estimatedMinutes} m
    </span>
  )
}

const DefaultProcessingSteps = ({ steps, estimatedMinutes = 2 }: ProcessingStepsProps) => {
  return (
    <div className={Styles.stepsContainer}>
      {steps.map((step) => (
        <div key={step.name} className={Styles.step}>
          <div className={Styles.stepRow}>
            <div className={Styles.stepIcon}>
              {step.status === 'completed' && (
                <div className={Styles.completedIcon}>
                  <Check02 color="white" size={10} />
                </div>
              )}
              {step.status === 'in_progress' && <div className={Styles.pendingIcon} />}
            </div>
            <Text variant="secondary" color="var(--dark-60)">
              {step.name}
            </Text>
          </div>
          {step.status === 'in_progress' && (
            <div className={Styles.stepMeta}>
              <Text variant="metadata" color="var(--dark-40)">
                Thinking...
              </Text>
              <Text variant="metadata" color="var(--dark-40)">
                <StepTimer estimatedMinutes={estimatedMinutes} />
              </Text>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

const NextGenProcessingSteps = ({ steps, estimatedMinutes, isCompleted }: ProcessingStepsProps) => {
  const [isExpanded, setIsExpanded] = useState(!isCompleted)
  let foundFirstInProgress = false

  return (
    <div className={Styles.collapsibleContainer}>
      <button className={Styles.headerRow} onClick={() => setIsExpanded((v) => !v)}>
        {isCompleted ? (
          <>
            <div className={Styles.completedIcon}>
              <Check02 color="white" size={10} />
            </div>
            <Text variant="secondary" color="var(--dark-60)">
              Request processed
            </Text>
            <div className={Styles.chevron}>
              {isExpanded ? (
                <ChevronDown size={16} color="var(--dark-40)" />
              ) : (
                <ChevronRightSmall size={16} color="var(--dark-40)" />
              )}
            </div>
          </>
        ) : (
          <>
            <Text variant="secondary" color="var(--dark-60)">
              Processing request...
            </Text>
            <div className={Styles.chevron}>
              {isExpanded ? (
                <ChevronUp size={16} color="var(--dark-40)" />
              ) : (
                <ChevronDown size={16} color="var(--dark-40)" />
              )}
            </div>
            <div className={Styles.timerRight}>
              <StepTimer estimatedMinutes={estimatedMinutes} isNextGen />
            </div>
          </>
        )}
      </button>
      {isExpanded && (
        <div className={Styles.stepsContainer}>
          {steps.map((step) => {
            let stepIcon: React.ReactNode
            if (step.status === 'completed') {
              stepIcon = (
                <div className={Styles.completedIcon}>
                  <Check02 color="white" size={10} />
                </div>
              )
            } else if (step.status === 'in_progress') {
              if (!foundFirstInProgress) {
                foundFirstInProgress = true
                stepIcon = <Spinner />
              } else {
                stepIcon = <div className={Styles.pendingIcon} />
              }
            }

            return (
              <div key={step.name} className={Styles.step}>
                <div className={Styles.stepRow}>
                  <div className={Styles.stepIcon}>{stepIcon}</div>
                  <Text variant="secondary" color="var(--dark-60)">
                    {step.name}
                  </Text>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export const ProcessingSteps = (props: ProcessingStepsProps) => {
  if (props.isNextGen) {
    return <NextGenProcessingSteps {...props} />
  }
  return <DefaultProcessingSteps {...props} />
}
